import React, { useEffect, useState } from "react";
import { Button, Col, Form, Image, Row, Spinner } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import TextInput from "components/TextInput";
import IMAGES from "constants/images";
import { Link, useNavigate } from "react-router-dom";
import { Sentry } from "react-activity";
import {
  getIndustriesList,
  getCompanySizesList,
  getRegionsList,
  getMyProfileDetails,
  updateProfile,
  fileUpload,
} from "api/authService";
import { toast } from "react-toastify";
import { useAuth } from "context/UserContext";
import Loader from "components/Loader";
import { Icon } from "@iconify/react";
const schema = yup.object().shape({
  firstName: yup.string() .trim()
  .required("First Name is required")
  .min(2, "First Name must be at least 2 characters")
  .max(30, "First Name cannot exceed 30 characters"),
  lastName: yup.string().trim()
  .required("Last Name is required")
  .min(2, "Last Name must be at least 2 characters")
  .max(30, "Last Name cannot exceed 30 characters"),
  email: yup.string().email("Invalid email").required("Email is required"),
  region: yup.string(),
  title: yup.string().trim(),
  companyName: yup.string().trim(),
  industry: yup.string(),
  companySize: yup.string(),
  linkedInProfileUrl: yup.string().url("Please enter a valid URL").trim(),
  twitterProfileUrl: yup.string().url("Please enter a valid URL").trim(),
  description: yup.string().trim(),
});

const ProfileDetailsForm = ({ onEdit, setShowModal }) => {

  const navigate = useNavigate();
  const [industries, setIndustries] = useState([]);
  const [companySizes, setCompanySizes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const { login: loginUser, user,setUpdateUser,refetchProfile } = useAuth();
  const [pageLoading, setPageLoading] = useState(true);

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const fetchData = async () => {
    try {
      setUpdateLoading(true); // Show loading state

      // First fetch all dropdown data
      const [industriesRes, companySizesRes, regionsRes] = await Promise.all([
        getIndustriesList(),
        getCompanySizesList(),
        getRegionsList(),
      ]);

      setIndustries(industriesRes.body.data);
      setCompanySizes(companySizesRes.body.data);
      setRegions(regionsRes.body.data);

      // Then fetch profile data after dropdowns are set
      const profileRes = await getMyProfileDetails();
      const profile = profileRes.body;
      setUpdateUser(profile)
      console.log("profile.companySize ", profile.companySize)
      // Set form values
      const formValues = {
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        region: profile.region,
        title: profile.title || "",
        companyName: profile.companyName || "",
        industry: profile.industry,
        companySize: profile.companySize,
        linkedInProfileUrl: profile.linkedInProfileUrl || "",
        twitterProfileUrl: profile.twitterProfileUrl || "",
        description: profile.description || "",
      };

      // Set all values at once
      Object.entries(formValues).forEach(([key, value]) => {
        setValue(key, value);
      });

      setImagePreview(profile.profileUrl || IMAGES.User2_Img);
      setPageLoading(false)
    } catch (err) {
      console.error("Error loading profile form:", err);
      toast.error("Failed to load profile data");
    } finally {
      setUpdateLoading(false);
    }
  };


  useEffect(() => {
    fetchData();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      return;
    }
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data) => {
    try {
      setUpdateLoading(true);
      let uploadedImageUrl = null;
      if (imageFile) {
        const res = await fileUpload(imageFile);
        uploadedImageUrl = res.body[0];
      }

      const rawPayload = {
        ...data,
        profileUrl: uploadedImageUrl || imagePreview,
      };

      const payload = Object.fromEntries(
        Object.entries(rawPayload).filter(
          ([_, value]) => value !== '' && value !== null && value !== undefined
        )
      );

      const response = await updateProfile(payload);
      if (response.success === true && response.code === 200) {
        const token = localStorage.getItem("authToken");
        loginUser(response.body, token); // store in context
        toast.success(response.message);
        refetchProfile();
      }
      setUpdateLoading(false);
      //   onEdit(); // close modal or show toast
    } catch (err) {
      setUpdateLoading(false);
      console.error("Update failed", err);
    }
  };

  return pageLoading ? (
    <div className="loaderdiv">
      <Sentry color="#e91515" size={40} speed={1} animating={true} />
      {/* <Loader /> */}
    </div>
  ) : (
    <>
      <div className="profile_details_style header_profile3">
        {/* <Icon icon="mdi:arrow-left" className="me-2" onClick={() => navigate("/")}/> */}
        <h3>Profile Details</h3>
      </div>
      <div className="d-flex align-items-center header_profile2 ">
        <Icon icon="mdi:arrow-left" style={{fontSize: "24px"}} className="me-4" onClick={() => navigate("/")}/>
        <h3 className="mb-0">Profile Details</h3>
      </div>
      
      <h5 className="mt-4">Personal Details</h5>
      <div className="profile_IMG position-relative">
        <Image src={imagePreview} className="profile_style" roundedCircle />
        <Form.Label className="edit_IMG">
          <input type="file" accept="image/*" hidden onChange={handleImageChange} />
          <Image src={IMAGES.Edit_Img} />
        </Form.Label>
      </div>

      <Form className="mt-3" onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>First Name</Form.Label>
              <Form.Control
                {...register("firstName")}
                isInvalid={!!errors.firstName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                {...register("lastName")}
                isInvalid={!!errors.lastName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3 mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                {...register("email")}
                isInvalid={!!errors.email}
                disabled
              />
              <Form.Control.Feedback type="invalid">
                {errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Region</Form.Label>
              <Form.Select {...register("region")} isInvalid={!!errors.region}>
                <option value="">Select Region</option>
                {regions.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.title}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.region?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <TextInput
          labelText="Description"
          textarea="textarea"
          rows={6}
          {...register("description")}
          errorMessage={errors.description?.message}
        />

        <h5 className="mt-4">Professional Details</h5>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Industry</Form.Label>
              <Form.Select
                {...register("industry")}
                isInvalid={!!errors.industry}
              >
                <option value="">Select Industry</option>
                {industries.map((i) => (
                  <option key={i._id} value={i._id}>
                    {i.title}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.industry?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Title</Form.Label>
              <Form.Control {...register("title")} isInvalid={!!errors.title} />
              <Form.Control.Feedback type="invalid">
                {errors.title?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Company Name</Form.Label>
              <Form.Control
                {...register("companyName")}
                isInvalid={!!errors.companyName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.companyName?.message}
              </Form.Control.Feedback>
            </Form.Group>

          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Company Size</Form.Label>
              <Form.Select
                {...register("companySize")}
                isInvalid={!!errors.companySize}
              >
                <option value="">Select Size</option>
                {companySizes.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.title} Employees
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.companySize?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <h5 className="mt-4">Social Links</h5>
        <Row className="social_links_style">
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>LinkedIn Profile URL</Form.Label>
              <Form.Control
                {...register("linkedInProfileUrl")}
                isInvalid={!!errors.linkedInProfileUrl}
              />
              <Form.Control.Feedback type="invalid">
                {errors.linkedInProfileUrl?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>Twitter Profile URL</Form.Label>
              <Form.Control
                {...register("twitterProfileUrl")}
                isInvalid={!!errors.twitterProfileUrl}
              />
              <Form.Control.Feedback type="invalid">
                {errors.twitterProfileUrl?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <div className="tab_1link">
          {user?.loginType === "normal" && (
            <Link onClick={() => setShowModal(true)}>
              Looking to change your password?
            </Link>
          )}
          <Button
            disabled={updateLoading}
            type="submit"
            variant="info"
            className="border-radius-100 "
            style={{maxWidth:"250px",width:"100%"}}
          >
            {updateLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Save"
            )}

          </Button>
        </div>
      </Form>
    </>
  );
};

export default ProfileDetailsForm;
